'use client';
import React from 'react';
import { useRequest } from 'ahooks';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Bell, ChevronsRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, type Notification, type UnifiedMessage } from '@/lib/api';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, TooltipArrow } from '@/components/ui/tooltip';
import { Popover, Tabs } from 'antd';
import { NotificationsItem } from '@/app/components/Explore/Notification/item';
import { getCategoryKey } from '@/app/components/Explore/Notification/category';
import { Loading } from '@/components/loading';
import { Empty } from '@/components/empty';
import { useUserInfo } from '@/app/stores/useUserStore';

export default function CoMessage() {
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  // 未读消息通知
  const [messageCount, setMessageCount] = useState(0);
  const [activeTab, setActiveTab] = useState('notifications');
  const { t } = useTranslation();

  // 全部消息 & 分类消息统一状态
  const [messages, setMessages] = useState<UnifiedMessage[]>([]);
  // 各分类未读数
  const [unreadCount, setUnreadCount] = useState<Record<string, number>>({});
  // 分页相关状态
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { run: getMessageCount } = useRequest(
    async () => {
      return await api.user.getMessageCountV2();
    },
    {
      manual: true,
      pollingInterval: 10000, // 10秒轮询
      onSuccess: res => {
        setMessageCount(res.data);
      },
    }
  );
  // 统一获取消息（全部 / 分类）
  const fetchMessages = async (tab: string, page: number = 1, append: boolean = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    try {
      if (tab === 'notifications') {
        const res = await api.user.getMessageSummaryV2({ page, limit: 10 });
        if (res.code === 200 && res.data) {
          const list = res.data.all || [];
          if (append) {
            setMessages(prev => [...prev, ...list]);
          } else {
            setMessages(list);
          }
          setCurrentPage(res.data.allCurrPage || page);
          setTotalPages(res.data.allTotalPage || 1);
          setHasMore((res.data.allCurrPage || page) < (res.data.allTotalPage || 1));
          setMessageCount(res.data.unreadCount?.all || 0);
          setUnreadCount(res.data.unreadCount || {});
        }
      } else {
        const res = await api.user.getMessagesV2({
          page,
          limit: 10,
          readStatus: 2,
          viewMode: 'grouped',
          category: tab,
        });
        if (res.code === 200 && res.data) {
          const d = res.data;
          if (append) {
            setMessages(prev => [...prev, ...(d.list || [])]);
          } else {
            setMessages(d.list || []);
          }
          setCurrentPage(d.currPage || page);
          setTotalPages(d.totalPage || 1);
          setHasMore((d.currPage || page) < (d.totalPage || 1));
        }
      }
    } catch (err) {
      console.error('获取消息失败:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 加载更多消息
  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    await fetchMessages(activeTab, nextPage, true);
  }, [activeTab, currentPage, hasMore, loadingMore]);

  // 滚动监听
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;

      // 距离底部50px时触发加载
      if (scrollHeight - scrollTop - clientHeight < 50) {
        loadMoreMessages();
      }
    },
    [loadMoreMessages]
  );

  const renderMessages = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loading />
        </div>
      );
    }
    if (messages.length === 0) {
      return <Empty className="bg-transparent">{t('empty.noData')}</Empty>;
    }
    let showedToday = false;
    let showedEarlier = false;
    return (
      <>
        {messages.map((item: UnifiedMessage) => {
          const isToday = dayjs(item.createTime).isSame(dayjs(), 'day');
          let separator = null;
          if (isToday && !showedToday) {
            showedToday = true;
            separator = (
              <div key="sep-today" className="text-sm font-semibold text-font-primary pb-2">
                {t('header.today')}
              </div>
            );
          } else if (!isToday && !showedEarlier) {
            showedEarlier = true;
            separator = (
              <div key="sep-earlier" className={`text-sm font-semibold text-font-primary pb-2${showedToday ? ' pt-4' : ''}`}>
                {t('header.earlier')}
              </div>
            );
          }
          return (
            <React.Fragment key={item.id}>
              {separator}
              <NotificationsItem
                isDropdown={true}
                notification={item as unknown as Notification}
                openNewTab={false}
                activeTab={activeTab}
                onItemClick={() => setOpen(false)}
                onRemove={(id: string | number) => {
                  const removed = messages.find(msg => msg.id === id);
                  setMessages(prev => prev.filter(msg => msg.id !== id));
                  setMessageCount(prev => Math.max(0, prev - 1));
                  if (removed) {
                    const categoryKey = getCategoryKey(removed.messageType);
                    if (categoryKey) {
                      const subtractNum =
                        categoryKey === 'interactions' && removed?.aggregateCount && removed?.aggregateCount > 0 ? removed?.aggregateCount : 1;
                      setUnreadCount(prev => ({
                        ...prev,
                        [categoryKey]: Math.max(0, (prev[categoryKey] || 0) - subtractNum),
                      }));
                    }
                  }
                }}
              />
            </React.Fragment>
          );
        })}
        {loadingMore && (
          <div className="text-center py-4 text-xs text-font-primary/50 flex items-center justify-center gap-1">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('header.loading')}
          </div>
        )}
        {!hasMore && messages.length > 0 && <div className="text-center py-4 text-xs text-font-primary/50">{t('header.noMoreData')}</div>}
      </>
    );
  };
  // 获取帖子信息
  useEffect(() => {
    getMessageCount();
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen) {
      fetchMessages('notifications', 1, false);
      return;
    }
    setMessages([]);
    setCurrentPage(1);
    setTotalPages(1);
    setHasMore(false);
    setActiveTab('notifications');
  };
  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      classNames={{
        container: '!p-0',
      }}
      content={
        <div className="w-[420px]">
          <Tabs
            activeKey={activeTab}
            size="small"
            items={[
              {
                key: 'notifications',
                label: t('header.all'),
              },
              {
                key: 'announcement',
                label: (
                  <span className="relative">
                    {t('header.official')}
                    {unreadCount.announcement > 0 && <span className="absolute -top-1 -right-2 size-1.5 rounded-full bg-red-500" />}
                  </span>
                ),
              },
              {
                key: 'system',
                label: (
                  <span className="relative">
                    {t('header.system')}
                    {unreadCount.system > 0 && <span className="absolute -top-1 -right-2 size-1.5 rounded-full bg-red-500" />}
                  </span>
                ),
              },
              {
                key: 'account',
                label: (
                  <span className="relative">
                    {t('header.account')}
                    {unreadCount.account > 0 && <span className="absolute -top-1 -right-2 size-1.5 rounded-full bg-red-500" />}
                  </span>
                ),
              },
              {
                key: 'interactions',
                label: (
                  <span className="relative">
                    {t('header.interactions')}
                    {unreadCount.interactions > 0 && <span className="absolute -top-1 -right-2 size-1.5 rounded-full bg-red-500" />}
                  </span>
                ),
              },
            ]}
            className="co-tabs co-message-tabs"
            tabBarExtraContent={{
              right: (
                <span
                  className="text-xs text-font-secondary cursor-pointer hover:text-font-primary flex items-center gap-0.5 pr-2"
                  onClick={() => {
                    const categoryParam = activeTab !== 'notifications' ? `/${activeTab}` : '';
                    navigate(`/${userInfo?.handle}/notifications${categoryParam}`);
                    setOpen(false);
                  }}
                >
                  {t('header.viewAll')}
                  <ChevronsRight className="size-3" />
                </span>
              ),
            }}
            onChange={(key: string) => {
              setActiveTab(key);
              // 重置分页状态
              setCurrentPage(1);
              setTotalPages(1);
              setHasMore(false);
              setMessages([]);
              fetchMessages(key, 1, false);
            }}
          />
          <div ref={scrollContainerRef} className="flex flex-col w-full max-h-[500px] overflow-y-auto p-2 px-4 relative z-1" onScroll={handleScroll}>
            {renderMessages()}
          </div>
        </div>
      }
    >
      <div className="relative hidden md:block">
        <div className="max-md:hidden transition-all flex items-center cursor-pointer gap-1 rounded-md bg-background-card border border-button-border/85 relative p-2 hover:border-button-border text-font-secondary hover:text-font-primary hover:bg-secondary-hover">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Bell className="w-4 h-4" />
              </TooltipTrigger>
              <TooltipContent className="TooltipContent" sideOffset={10} arrowPadding={10}>
                {t('header.notification')}
                <TooltipArrow className="TooltipArrow" />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {messageCount > 0 && (
          <div
            className="bg-red-500 rounded-full w-2 h-2 absolute top-0 z-10"
            style={{
              insetInlineEnd: 0,
              transform: 'translate(40%, -50%)',
            }}
          />
        )}
      </div>
    </Popover>
  );
}
